import { useState, useEffect } from "react";
import { apiClient } from "../App";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Shield, AlertTriangle, ChevronRight, Award, Clock } from "lucide-react";

const BAND_STYLES = {
  ready: { bg: "bg-emerald-50", border: "border-emerald-200",
            ring: "ring-emerald-300", text: "text-emerald-700",
            chip: "bg-emerald-600 text-white", bar: "bg-emerald-500" },
  nearly_ready: { bg: "bg-sky-50", border: "border-sky-200",
                   ring: "ring-sky-300", text: "text-sky-700",
                   chip: "bg-sky-600 text-white", bar: "bg-sky-500" },
  action_required: { bg: "bg-amber-50", border: "border-amber-200",
                      ring: "ring-amber-300", text: "text-amber-800",
                      chip: "bg-amber-600 text-white", bar: "bg-amber-500" },
  blocked: { bg: "bg-rose-50", border: "border-rose-200",
              ring: "ring-rose-300", text: "text-rose-700",
              chip: "bg-rose-600 text-white", bar: "bg-rose-500" },
};

const SEV_STYLES = {
  critical: "bg-rose-100 text-rose-700 border-rose-200",
  high: "bg-amber-100 text-amber-800 border-amber-200",
  medium: "bg-sky-100 text-sky-700 border-sky-200",
  low: "bg-slate-100 text-slate-700 border-slate-200",
};

/**
 * Fundraiser-facing Passport Score card (OCI V2.1A).
 * Live score, status band, outstanding actions, expiry forecast,
 * one-click "Complete My Passport" CTA.
 */
export function PassportScoreCard({ userId, campaignId, onCompletePassport }) {
  const [passport, setPassport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!userId) {
        if (active) { setPassport(null); setLoading(false); }
        return;
      }
      try {
        const params = campaignId ? `?campaignId=${campaignId}` : "";
        const r = await apiClient.get(
          `/workforce/users/${userId}/passport${params}`
        );
        if (active) setPassport(r.data);
      } catch (e) {
        if (active) setPassport(null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [userId, campaignId]);

  if (loading) {
    return (
      <Card className="border-slate-200" data-testid="passport-score-card-loading">
        <CardContent className="p-5">
          <div className="h-24 animate-pulse bg-slate-100 rounded-lg" />
        </CardContent>
      </Card>
    );
  }
  if (!passport) return null;

  const band = BAND_STYLES[passport.passportStatus] || BAND_STYLES.action_required;
  const score = passport.passportScore ?? 0;
  const actions = passport.passportActions || [];
  const expiry = (passport.expiryForecast || []).filter(
    e => e.status === "expiring_soon" || e.status === "expired"
  );

  return (
    <Card
      className={`${band.bg} ${band.border} shadow-none`}
      data-testid="passport-score-card"
    >
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-full ${band.chip}`}>
              <Shield className="h-5 w-5" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">
                Fundraiser Passport
              </div>
              <div className={`font-heading font-bold text-lg ${band.text}`}>
                {passport.passportLabel}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div
              className={`text-3xl font-bold ${band.text}`}
              data-testid="passport-score-value"
            >
              {score}%
            </div>
            <div className="text-xs text-slate-500">Score</div>
          </div>
        </div>

        {/* Bar */}
        <div className="w-full bg-white/60 rounded-full h-2 mb-4 overflow-hidden">
          <div
            className={`${band.bar} h-full rounded-full transition-all duration-500`}
            style={{ width: `${score}%` }}
            data-testid="passport-score-bar"
          />
        </div>

        {/* Outstanding Actions */}
        {actions.length > 0 ? (
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-4 w-4 text-slate-600" />
              <div className="font-semibold text-sm text-slate-800">
                Outstanding Actions ({actions.length})
              </div>
            </div>
            <ul className="space-y-1.5" data-testid="passport-actions-list">
              {actions.slice(0, 5).map((a, i) => (
                <li
                  key={i}
                  className="flex items-center justify-between bg-white/70 rounded-lg px-3 py-2 border border-white"
                  data-testid={`passport-action-${a.action}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className={`text-[10px] uppercase font-bold rounded-md px-2 py-0.5 border ${SEV_STYLES[a.severity] || SEV_STYLES.low}`}
                    >
                      {a.severity}
                    </span>
                    <span className="text-sm text-slate-800 truncate">
                      {a.label}
                    </span>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
                </li>
              ))}
            </ul>
            {actions.length > 5 && (
              <div className="text-xs text-slate-500 mt-2 text-center">
                +{actions.length - 5} more…
              </div>
            )}
          </div>
        ) : (
          <div className="mb-4 flex items-center gap-2 bg-white/70 rounded-lg px-3 py-2.5 border border-emerald-200">
            <Award className="h-4 w-4 text-emerald-600" />
            <span className="text-sm font-semibold text-emerald-700">
              All clear &mdash; you&apos;re ready to fundraise.
            </span>
          </div>
        )}

        {/* Expiry forecast */}
        {expiry.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-4 w-4 text-slate-600" />
              <div className="font-semibold text-sm text-slate-800">
                Expiry Watch
              </div>
            </div>
            <div className="space-y-1.5">
              {expiry.map((e, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between bg-white/70 rounded-lg px-3 py-2 border border-white text-sm"
                  data-testid={`passport-expiry-${e.field}`}
                >
                  <span className="font-medium text-slate-700 uppercase text-xs">
                    {e.field}
                  </span>
                  <span
                    className={
                      e.status === "expired"
                        ? "text-rose-700 font-semibold"
                        : "text-amber-700 font-semibold"
                    }
                  >
                    {e.status === "expired" ? "Expired" : "Expiring soon"}
                    {e.daysUntilExpiry != null &&
                      e.status !== "expired" &&
                      ` · ${e.daysUntilExpiry}d`}
                    {e.monthsUntilExpiry != null &&
                      e.status !== "expired" &&
                      ` · ${e.monthsUntilExpiry}m`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        {actions.length > 0 && (
          <Button
            className="w-full bg-slate-900 hover:bg-slate-800 text-white"
            onClick={() => onCompletePassport && onCompletePassport(actions[0])}
            data-testid="complete-my-passport-btn"
          >
            Complete My Passport
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
